import React, { useState, useCallback, useEffect } from 'react';
import {
  Modal,
  Icon,
  Text,
  BlockStack,
  TextField,
  Autocomplete,
  DropZone,
} from '@shopify/polaris';
import {
  MagicMajor,
  UploadMajor,
  SearchMinor,
} from '@shopify/polaris-icons';
import { BlobServiceClient } from '../../services/blobStorageCompat';
import { authService } from '../../services/authService';
import { storiesService } from '../../services/storiesService';

const SERVER_URL = process.env.REACT_APP_SERVER_URL;

const CreatePreviewerModal = ({ 
  isOpen, 
  onClose, 
  setToastMessage, 
  setToastActive,
  onPreviewerCreated 
}) => {
  const [isCreatingPreviewer, setIsCreatingPreviewer] = useState(false);
  const [previewerFiles, setPreviewerFiles] = useState([]);
  const [newPreviewerData, setNewPreviewerData] = useState({
    kid_name: '',
    slug: '',
    kid_image: '',
    generation_id: ''
  });
  const [previewerStoryAutocompleteInputValue, setPreviewerStoryAutocompleteInputValue] = useState('');
  const [selectedStoryForPreviewer, setSelectedStoryForPreviewer] = useState([]);
  const [isLoadingStories, setIsLoadingStories] = useState(false);
  const [stories, setStories] = useState([]);
  const [azureSettings, setAzureSettings] = useState(null);
  const [previewerStoryAutocompleteOptions, setPreviewerStoryAutocompleteOptions] = useState([]);

  // Stories functions
  const fetchStories = useCallback(async () => {
    setIsLoadingStories(true);
    try {
      const data = await storiesService.getAllStories();
      const storiesArray = Array.isArray(data) ? data : [];
      setStories(storiesArray);
    } catch (err) {
      console.error('Fetch error:', err);
      setStories([]); // Ensure stories is always an array
    } finally {
      setIsLoadingStories(false);
    }
  }, []);

  // Upload file to Azure Blob using Azure SDK
  const uploadFileToAzure = useCallback(async (file, _generationId, kidName, storyName) => {
    if (!azureSettings) {
      throw new Error('Azure settings not loaded');
    }
    const { AZURE_BLOB_BASE_URL, AZURE_SAS_TOKEN } = azureSettings;
    const containerUrl = AZURE_BLOB_BASE_URL + AZURE_SAS_TOKEN;
    const blobServiceClient = new BlobServiceClient(containerUrl);
    const containerClient = blobServiceClient.getContainerClient("");
    // Sanitize and build the new file name
    const sanitize = str => (str || '').replace(/[^a-zA-Z0-9]/g, '_');
    const ext = file.name.split('.').pop();
    // Generate RandomTXT: short random string + timestamp
    const randomTxt = `${Math.random().toString(36).substring(2, 6)}${Date.now()}`;
    const newFileName = `${sanitize(randomTxt)}_${sanitize(kidName)}_${sanitize(storyName)}.${ext}`;
    // Remove the 'faces/' prefix, upload directly to the root
    const blobName = newFileName;
    const blockBlobClient = containerClient.getBlockBlobClient(blobName);
    const { publicUrl } = await blockBlobClient.uploadBrowserData(file, {
      blobHTTPHeaders: { blobContentType: file.type }
    });
    return publicUrl;
  }, [azureSettings]);

  // Load Azure settings from API
  const loadAzureSettings = useCallback(async () => {
    try {
      const response = await fetch(`${SERVER_URL}/settings/storage/client`, {
        headers: authService.getAuthHeaders(),
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data && result.data.length > 0) {
          setAzureSettings(result.data[0]);
        }
      } else {
        console.error('Failed to load Azure settings:', response.status);
      }
    } catch (error) {
      console.error('Error loading Azure settings:', error);
    }
  }, []);

  // Load Azure settings on initial mount
  useEffect(() => {
    if (isOpen) {
      loadAzureSettings();
      fetchStories();
    }
  }, [isOpen, loadAzureSettings, fetchStories]);

  // Handle story autocomplete change for previewer
  const handlePreviewerStoryAutocompleteChange = useCallback((value) => {
    setPreviewerStoryAutocompleteInputValue(value);
    if (value === '') {
      const allOptions = stories.map(story => ({ label: story.title, value: story.slug }));
      setPreviewerStoryAutocompleteOptions(allOptions);
      return;
    }
    const filterRegex = new RegExp(value, 'i');
    const filteredOptions = stories
      .filter(story => story.title.match(filterRegex) || story.slug.match(filterRegex))
      .map(story => ({ label: story.title, value: story.slug }));
    setPreviewerStoryAutocompleteOptions(filteredOptions);
  }, [stories]);

  // Handle story autocomplete select for previewer
  const handlePreviewerStoryAutocompleteSelect = useCallback((selected) => {
    setSelectedStoryForPreviewer(selected);
    const selectedSlug = selected[0] || '';
    setNewPreviewerData(prev => ({ ...prev, slug: selectedSlug }));
    const selectedStory = stories.find(story => story.slug === selectedSlug);
    setPreviewerStoryAutocompleteInputValue(selectedStory ? selectedStory.title : '');
  }, [stories]);

  // Handle Create Previewer
  const handleCreatePreviewer = useCallback(async () => {
    if (!newPreviewerData.kid_name || !newPreviewerData.slug || previewerFiles.length === 0) {
      setToastMessage('Please fill in all required fields and upload a kid image');
      setToastActive(true);
      setTimeout(() => {
        setToastActive(false);
      }, 3000);
      return;
    }

    if (newPreviewerData.kid_name.length > 20) {
      setToastMessage('Kid name must be 20 characters or less');
      setToastActive(true);
      setTimeout(() => {
        setToastActive(false);
      }, 3000);
      return;
    }

    setIsCreatingPreviewer(true);
    try {
      // Upload file to Azure
      const uploadedImageUrl = await uploadFileToAzure(previewerFiles[0], newPreviewerData.generation_id, newPreviewerData.kid_name, previewerStoryAutocompleteInputValue);
      console.log('Azure uploaded image URL:', uploadedImageUrl);
      
      // Generate random number for generation_id
      const timestamp = Date.now();
      const randomSuffix = Math.random().toString(36).substring(2, 8);
      const generationId = `PRVDSH${timestamp}-${randomSuffix}`;

      const requestData = {
        slug: newPreviewerData.slug,
        kid_name: newPreviewerData.kid_name,
        kid_image: uploadedImageUrl,
        generation_id: generationId
      };

      const response = await fetch(`${SERVER_URL}/previewer-stories/process`, {
        method: 'POST',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify(requestData),
      });

      if (response.ok) {
        await response.json(); // Just consume the response
        setToastMessage('Previewer Created Successfully');
        setToastActive(true);
        setTimeout(() => {
          setToastActive(false);
        }, 3000);
        
        // Reset form
        setNewPreviewerData({
          kid_name: '',
          slug: '',
          kid_image: '',
          generation_id: ''
        });
        setPreviewerFiles([]);
        setSelectedStoryForPreviewer([]);
        setPreviewerStoryAutocompleteInputValue('');
        onClose();
        
        // Notify parent component to refresh previewers list
        if (onPreviewerCreated) {
          onPreviewerCreated();
        }
      } else {
        let errorData = {};
        try {
          errorData = await response.json();
        } catch (parseError) {
          console.error('Failed to parse error response:', parseError);
        }
        
        const errorMessage = errorData.error || errorData.message || `HTTP error! status: ${response.status}`;
        setToastMessage(`Error: ${errorMessage}`);
        setToastActive(true);
        setTimeout(() => {
          setToastActive(false);
        }, 5000);
      }
    } catch (error) {
      console.error('Error creating previewer:', error);
      const errorMessage = error.message || 'Failed to create previewer';
      setToastMessage(`Error: ${errorMessage}`);
      setToastActive(true);
      setTimeout(() => {
        setToastActive(false);
      }, 5000);
    } finally {
      setIsCreatingPreviewer(false);
    }
  }, [newPreviewerData, previewerFiles, uploadFileToAzure, previewerStoryAutocompleteInputValue, onClose, onPreviewerCreated, setToastMessage, setToastActive]);

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title={<div style={{padding: '16px'}}>{'Create New Previewer'}</div>}
      primaryAction={{
        content: (
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Icon source={MagicMajor} tone="inverse" color="white" />
            Generate
          </span>
        ),
        onAction: handleCreatePreviewer,
        loading: isCreatingPreviewer,
      }}
      secondaryActions={[
        {
          content: 'Cancel',
          onAction: onClose,
        },
      ]}
      footer={<div style={{padding: '16px'}}></div>}
    >
      <BlockStack gap="400">
        {/* Kid Image Upload */}
        <div>
          <Text variant="bodyMd" as="label" style={{ display: 'block', marginBottom: 8 }}>Upload Kid Image</Text>
          <DropZone
            onDrop={(_dropFiles, acceptedFiles) => {
              // Only allow one image
              if (acceptedFiles.length > 0) {
                setPreviewerFiles([acceptedFiles[0]]);
              }
            }}
            accept="image/jpeg, image/png"
            variableHeight
            style={{
              margin: '16px 0',
              padding: '32px 0',
              display: 'block',
              width: '100%',
              textAlign: 'center'
            }}
          >
            {previewerFiles.length > 0 ? (
              <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, width: '100%' }}>
                {/* Red X button at top right */}
                <button
                  onClick={() => setPreviewerFiles([])}
                  style={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    background: '#dc2626',
                    color: 'white',
                    border: 'none',
                    borderRadius: '50%',
                    width: 28,
                    height: 28,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    fontSize: 20,
                    fontWeight: 'bold',
                    zIndex: 2
                  }}
                  aria-label="Remove image"
                >
                  ×
                </button>
                <img
                  src={window.URL.createObjectURL(previewerFiles[0])}
                  alt={previewerFiles[0].name}
                  style={{
                    maxWidth: '250px',
                    maxHeight: '250px',
                    width: '100%',
                    height: 'auto',
                    borderRadius: 12,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    objectFit: 'contain',
                    marginTop: 4
                  }}
                />
                <Text variant="bodyMd" as="p" style={{ margin: 0 }}>{previewerFiles[0].name}</Text>
                <Text variant="bodySm" tone="subdued" style={{ marginBottom: 20 }}>
                  {(previewerFiles[0].size / (1024 * 1024)).toFixed(2)} MB
                </Text>
              </div>
            ) : (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 12,
                width: '100%',
                padding: '16px 0'
              }}>
                <Icon source={UploadMajor} tone="base" />
                <Text variant="bodyMd" as="p" style={{ margin: 0 }}>Add Image</Text>
                <Text variant="bodySm" tone="subdued" style={{ margin: 0 }}>Accepts .jpg, and .png</Text>
              </div>
            )}
          </DropZone>
        </div>

        {/* Kid Name */}
        <TextField
          label="Kid Name"
          value={newPreviewerData.kid_name}
          onChange={(value) => setNewPreviewerData(prev => ({ ...prev, kid_name: value }))}
          placeholder="Enter kid name (max 20 characters)"
          maxLength={20}
          required
        />

        {/* Story Selection */}
        <Autocomplete
          options={previewerStoryAutocompleteOptions}
          selected={selectedStoryForPreviewer}
          onSelect={handlePreviewerStoryAutocompleteSelect}
          loading={isLoadingStories}
          textField={
            <Autocomplete.TextField
              onChange={handlePreviewerStoryAutocompleteChange}
              label="Story"
              value={previewerStoryAutocompleteInputValue}
              prefix={<Icon source={SearchMinor} tone="base" />}
              placeholder="Search for a story..."
              autoComplete="off"
              required
            />
          }
        />
      </BlockStack>
    </Modal>
  );
};

export default CreatePreviewerModal; 